import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

const floatBlob = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, -30px) scale(1.05); }
`;

export const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
  color: #1e293b;
  font-family: "Outfit", "Inter", sans-serif;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
`;

export const Blob1 = styled.div`
  position: absolute;
  top: -10%;
  left: -10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(242, 50, 54, 0.03) 0%, rgba(242, 50, 54, 0) 70%);
  border-radius: 50%;
  filter: blur(80px);
  animation: ${floatBlob} 20s ease-in-out infinite;
  pointer-events: none;
`;

export const Blob2 = styled.div`
  position: absolute;
  bottom: -10%;
  right: -10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(181, 35, 38, 0.02) 0%, rgba(181, 35, 38, 0) 70%);
  border-radius: 50%;
  filter: blur(80px);
  animation: ${floatBlob} 20s ease-in-out infinite alternate;
  pointer-events: none;
`;

// 🔹 Navigation Header
export const Header = styled.header`
  width: 100%;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
  position: relative;
  z-index: 100;
  box-sizing: border-box;
`;

export const HeaderInner = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
`;


export const Logo = styled.img`
  height: 32px;
  object-fit: contain;
  cursor: pointer;
`;

export const NavLinks = styled.nav`
  display: flex;
  gap: 24px;
`;

export const NavLink = styled.a`
  color: #64748b;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #ff3135;
  }
`;

// 🔹 Content Wrapper (Clean Full-page document style)
export const ContentWrapper = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 60px 24px 100px;
  box-sizing: border-box;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 40px 20px 60px;
  }
`;

export const PageTitle = styled.h1`
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.2;
  margin: 0 0 12px 0;
  color: #0f172a;

  @media (max-width: 600px) {
    font-size: 30px;
  }
`;

export const UpdatedDate = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 0 0 40px 0;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 24px;
`;

export const HtmlContent = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #334155;

  p {
    margin: 0 0 20px 0;
  }

  h2 {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 36px 0 16px 0;
    letter-spacing: -0.4px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin: 28px 0 12px 0;
  }

  ul, ol {
    margin: 0 0 24px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    color: #0f172a;
    font-weight: 600;
  }

  a {
    color: #ff3135;
    text-decoration: none;
    transition: text-decoration 0.2s ease;

    &:hover {
      text-decoration: underline;
    }
  }

  table {
    display: block;
    width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    margin: 28px 0;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  }

  tr {
    border-bottom: 1px solid #e2e8f0;
    
    &:last-child {
      border-bottom: none;
    }
  }

  th, td {
    padding: 14px 18px;
    border-right: 1px solid #e2e8f0;
    min-width: 150px;
    font-size: 14px;
    line-height: 1.5;
    text-align: left;
    vertical-align: top;
    
    &:last-child {
      border-right: none;
    }
  }

  th {
    background-color: #f8fafc;
    font-weight: 600;
    color: #0f172a;
  }

  td {
    color: #475569;
    
    strong {
      color: #0f172a;
    }
  }
`;

// 🔹 Contact Info Grid (Light mode)
export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const ContactCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ContactLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const ContactValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
`;

// 🔹 Loader styling inside document
export const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #64748b;
  font-size: 15px;
  font-weight: 500;
`;

// 🔹 Footer Section
export const FooterContainer = styled.footer`
  width: 100%;
  border-top: 1px solid #f1f5f9;
  background-color: #ffffff;
  position: relative;
  z-index: 10;
  padding: 40px 24px;
  box-sizing: border-box;
  margin-top: auto;
`;

export const Copyright = styled.p`
  font-size: 13px;
  color: #64748b;
  text-align: center;
  margin: 0;
`;
